import { useWebsocketContext } from '@providers/WebsocketProvider';
import { useAdminJobDetailsSubscription } from '@pages/Admin/JobDetailsPage/hooks/useAdminJobDetailsSubscription';
import { isEnglishChatMessage } from './helpers/isEnglishChatMessage';
import { cn } from '@helpers/cn';
import type { CreateSuperEnhancementMessageBodyDto, SuperEnhancementChatMessageResponseDto } from 'app/API/models';
import { createFetchSuperEnhancementMessagesQueryFn } from 'app/queries/superEnhancement/createFetchSuperEnhancementMessagesQueryFn';
import { sortSuperEnhancementMessages } from './helpers/sortSuperEnhancementMessages';
import { getSuperEnhancementStageOptions } from './helpers/getSuperEnhancementStageOptions';
import { Header } from '@components/Header';
import { LinearProgressLoader } from '@components/core-ui/spinners/LinearProgressLoader';
import { AudioPlayer, type AudioPlayerHandle } from '@components/AudioPlayer';
import { Input } from '@components/core-ui/form-elements/Input';
import { IconButtonClean } from '@components/core-ui/form-elements/buttons/IconButton/variations/IconButtonClean';
import SendIcon from '@components/icons/SendIcon.svg';
import { Typing } from '@pages/Admin/JobDetailsPage/components/Content/components/MainContent/components/Typing';
import { TranscriptLayout } from '@pages/Admin/TranscriptPlayer/components/Content/components/TranscriptLayout';
import {
  buildTranscriptComparison,
  CompareTranscriptLayout,
} from '@pages/Admin/TranscriptPlayer/components/Content/components/CompareTranscriptLayout';
import { useAPI } from '@providers/useAPI';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFetchSuperEnhancementQueryFn } from 'app/queries/superEnhancement/createFetchSuperEnhancementQueryFn';
import { superEnhancementApi } from 'app/queries/superEnhancement/superEnhancementApi';
import { superEnhancementKeys } from 'app/queries/superEnhancement/superEnhancementKeys';
import { createFetchTranscriptAudioQueryFn } from 'app/queries/transcriptPlayer/createFetchTranscriptAudioQueryFn';
import { transcriptPlayerKeys } from 'app/queries/transcriptPlayer/transcriptPlayerKeys';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import toast from 'react-hot-toast';
import { useParams } from 'react-router-dom';

export const AdminSuperEnhancementPage = () => {
  const { id: jobId = '' } = useParams();
  const { call } = useAPI();
  const queryClient = useQueryClient();
  const { websocketSessionId, offline } = useWebsocketContext();
  useAdminJobDetailsSubscription(websocketSessionId, jobId);
  const [draft, setDraft] = useState('');
  const isDraftEnglish = isEnglishChatMessage(draft);
  const [showChanges, setShowChanges] = useState(true);
  const [playbackTime, setPlaybackTime] = useState(0);
  const [audioAttempt, setAudioAttempt] = useState(0);
  const audioRef = useRef<AudioPlayerHandle>(null);
  const [isStartingRun, setIsStartingRun] = useState(false);
  const runStartedRef = useRef<{ jobId: string; previousRunId?: string }>();
  const previousStatusRef = useRef<string>();
  const stateQuery = useQuery({
    queryKey: superEnhancementKeys.detail(jobId),
    queryFn: createFetchSuperEnhancementQueryFn(call),
    enabled: Boolean(jobId),
    refetchInterval: (query) => (query.state.data?.status === 'RUNNING' || isStartingRun ? 10000 : false),
  });
  const audioQuery = useQuery({
    queryKey: transcriptPlayerKeys.audio(jobId, audioAttempt),
    queryFn: createFetchTranscriptAudioQueryFn(call),
    enabled: Boolean(jobId),
    staleTime: Infinity,
  });
  const historyRef = useRef<HTMLDivElement>(null);
  const messagesQuery = useQuery({
    queryKey: superEnhancementKeys.messages(jobId),
    queryFn: createFetchSuperEnhancementMessagesQueryFn(call),
    enabled: Boolean(jobId),
    refetchInterval: () => (stateQuery.data?.status === 'RUNNING' || offline || isStartingRun ? 10000 : false),
  });
  const messages = useMemo(() => sortSuperEnhancementMessages(messagesQuery.data ?? []), [messagesQuery.data]);
  const stageOptions = useMemo(() => getSuperEnhancementStageOptions(messages), [messages]);
  const latestMessage = messages.at(-1);
  const confirmationId = latestMessage?.confirmationId;
  const customInstructions = messages.filter(
    (message) => Boolean(message.customInstruction) && !message.isInstructionRemoved,
  );
  useEffect(() => {
    if (historyRef.current) {
      historyRef.current.scrollTop = historyRef.current.scrollHeight;
    }
  }, [messages]);
  const messageMutation = useMutation({
    mutationFn: (message: CreateSuperEnhancementMessageBodyDto) =>
      call(() => superEnhancementApi.createMessage(jobId, message), {
        dontRedirectOnError: true,
        onError: (message) => toast.error(message),
      }),
    onSuccess: async (message) => {
      queryClient.setQueryData<SuperEnhancementChatMessageResponseDto[]>(
        superEnhancementKeys.messages(jobId),
        (previous = []) =>
          sortSuperEnhancementMessages([...previous.filter((entry) => entry.id !== message.id), message]),
      );
      setDraft('');
      await messagesQuery.refetch();
    },
  });
  const deleteInstructionMutation = useMutation({
    mutationFn: (messageId: string) =>
      call(() => superEnhancementApi.deleteInstruction(jobId, messageId), {
        dontRedirectOnError: true,
        onError: (message) => toast.error(message),
      }),
    onSuccess: async () => {
      await messagesQuery.refetch();
    },
  });
  const startMutation = useMutation({
    mutationFn: (reviewId: string) =>
      call(() => superEnhancementApi.start(jobId, { confirmationId: reviewId, messageId: crypto.randomUUID() }), {
        dontRedirectOnError: true,
        onError: (message) => toast.error(message),
      }),
    onSuccess: async () => {
      runStartedRef.current = { jobId, previousRunId: stateQuery.data?.runId };
      setIsStartingRun(true);
      queryClient.setQueryData(superEnhancementKeys.detail(jobId), (previous: typeof stateQuery.data) =>
        previous ? { ...previous, status: 'RUNNING' } : previous,
      );
      setDraft('');
      await Promise.all([stateQuery.refetch(), messagesQuery.refetch()]);
    },
  });
  const state = stateQuery.data;
  const previousRevision = useRef<{ jobId: string; runId?: string; revision: number }>();
  const [animatedRevision, setAnimatedRevision] = useState<number>();
  useEffect(() => {
    if (!state) {
      return;
    }
    const revision = state.revision ?? 0;
    const previous = previousRevision.current;
    if (
      state.status !== 'RUNNING' ||
      previous?.jobId !== jobId ||
      previous.runId !== state.runId ||
      revision < previous.revision
    ) {
      setAnimatedRevision(undefined);
    } else if (revision > previous.revision) {
      setAnimatedRevision(revision);
    }
    previousRevision.current = { jobId, runId: state.runId, revision };
  }, [jobId, state]);
  useEffect(() => {
    if (!isStartingRun) {
      return;
    }
    const started = runStartedRef.current;
    if (!state || !started || started.jobId !== jobId) {
      setIsStartingRun(false);
      return;
    }
    const hasRunIds = started.previousRunId !== undefined || state.runId !== undefined;
    const sawNewRun = state.runId !== undefined && state.runId !== started.previousRunId;
    const isTerminal = state.status === 'COMPLETED' || state.status === 'FAILED';
    const runRegistered = hasRunIds ? sawNewRun : state.status === 'RUNNING' || isTerminal;
    if (runRegistered) {
      setIsStartingRun(false);
    }
  }, [isStartingRun, state, jobId]);
  useEffect(() => {
    const previousStatus = previousStatusRef.current;
    previousStatusRef.current = state?.status;
    if (previousStatus === 'RUNNING' && state?.status !== undefined && state.status !== 'RUNNING') {
      void queryClient.invalidateQueries({ queryKey: superEnhancementKeys.messages(jobId) });
    }
  }, [state?.status, jobId, queryClient]);
  const isRunning = startMutation.isPending || isStartingRun || state?.status === 'RUNNING';
  const isBusy = isRunning || messageMutation.isPending || deleteInstructionMutation.isPending;
  const comparison = useMemo(
    () =>
      state && (state.status === 'COMPLETED' || (state.revision ?? 0) > 0)
        ? buildTranscriptComparison(state.sourceUtterances, state.utterances)
        : [],
    [state],
  );
  const seekTo = (timestamp: number) => audioRef.current?.seekTo(timestamp);
  const sendMessage = async (message = draft) => {
    if (isBusy || !state || stateQuery.isError || messagesQuery.isPending || messagesQuery.isError || !message.trim()) {
      return;
    }
    const content = message.trim();
    if (!isEnglishChatMessage(content)) {
      toast.error('Please write your message in English only.');
      return;
    }
    if (confirmationId && ['yes', 'yes, run'].includes(content.toLowerCase())) {
      try {
        await startMutation.mutateAsync(confirmationId);
      } catch {
        await messagesQuery.refetch();
      }
      return;
    }
    try {
      await messageMutation.mutateAsync({ id: crypto.randomUUID(), content });
    } catch {
      await messagesQuery.refetch();
    }
  };

  return (
    <>
      <Helmet>
        <title>Super Enhancement - AutoScript Admin</title>
      </Helmet>
      <Header title="Super Enhancement" />
      <div className="flex min-w-0 flex-col gap-6 p-4 xl:p-6 xl:pr-[384px]">
        <main className="flex min-h-0 min-w-0 flex-col gap-4 [--transcript-toolbar-clearance:0px]">
          {stateQuery.isPending && <LinearProgressLoader active={stateQuery.isPending} />}
          {stateQuery.isError && (
            <div role="alert">
              Unable to load the main transcript or enhancement status.{' '}
              <button type="button" onClick={() => void stateQuery.refetch()}>
                Retry
              </button>
            </div>
          )}
          {audioQuery.data?.signedUrl && (
            <AudioPlayer
              ref={audioRef}
              className="self-start !max-w-[720px] [&_.progress]:justify-start"
              src={audioQuery.data.signedUrl}
              onPlay={setPlaybackTime}
              onError={() => {
                if (audioAttempt === 0) {
                  setAudioAttempt(1);
                }
              }}
            />
          )}
          {audioQuery.isError && <p role="alert">Audio is unavailable.</p>}
          {(state?.status === 'COMPLETED' || (state?.revision ?? 0) > 0) && (
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={showChanges} onChange={(event) => setShowChanges(event.target.checked)} />
              Show changes from the original transcript
            </label>
          )}
          {(state?.status === 'COMPLETED' || (state?.revision ?? 0) > 0) && showChanges ? (
            <CompareTranscriptLayout
              utterances={comparison}
              playbackTime={playbackTime}
              onWordClick={seekTo}
              maxColorTimeDeltaMs={2000}
              animateChanges={
                state?.status === 'RUNNING' && animatedRevision !== undefined && animatedRevision === state.revision
              }
            />
          ) : state ? (
            <TranscriptLayout
              utterances={state.utterances}
              playbackTime={playbackTime}
              onWordClick={seekTo}
              searchNamePrefix="super-enhancement"
              title="Main transcript"
              height="calc(100vh - 260px)"
            />
          ) : null}
        </main>
        <aside
          aria-label="Super enhancement chat"
          lang="en"
          className="flex h-[500px] max-h-[calc(100dvh-2rem)] min-w-0 w-full flex-col gap-4 rounded-[10px] border border-border bg-[var(--color-surface)] p-5 shadow-lg xl:fixed xl:bottom-24 xl:right-0 xl:top-48 xl:z-10 xl:h-auto xl:max-h-none xl:w-[360px] xl:rounded-[10px]"
        >
          <h2 className="text-lg font-semibold text-[var(--color-title)]">Instructions</h2>
          <div
            ref={historyRef}
            aria-live="polite"
            role="log"
            className="flex min-h-0 flex-1 flex-col gap-4 overflow-auto text-sm"
          >
            {messagesQuery.isPending && <p role="status">Loading chat history…</p>}
            {messagesQuery.isError && (
              <p role="alert">
                Unable to load chat history.{' '}
                <button type="button" onClick={() => void messagesQuery.refetch()}>
                  Retry
                </button>
              </p>
            )}
            {messages.map((message) => (
              <div
                key={message.id}
                className={cn(
                  'w-[350px] max-w-full shrink-0 rounded-[10px] border border-border px-[15px] py-[10px]',
                  message.role === 'user' && 'self-end bg-[var(--color-surface-disabled)]',
                )}
              >
                <p
                  className={cn(
                    'whitespace-pre-wrap break-words',
                    message.isInstructionRemoved && 'line-through opacity-50',
                  )}
                >
                  {message.content}
                </p>
                {message.customInstruction && (
                  <div className="mt-2 flex items-center justify-between gap-2 text-xs">
                    <span className="text-[var(--color-text-muted)]">
                      {message.instructionStage === 'speakers'
                        ? 'Speaker correction'
                        : message.instructionStage === 'editing'
                          ? 'Text editing'
                          : 'Custom'}
                    </span>
                    <button
                      type="button"
                      disabled={isBusy}
                      aria-label={`Remove instruction: ${message.content}`}
                      onClick={() => deleteInstructionMutation.mutate(message.id)}
                      className="rounded border border-border px-2 py-1 disabled:opacity-50"
                    >
                      Remove
                    </button>
                  </div>
                )}
                {message.isInstructionRemoved && (
                  <p className="mt-1 text-xs text-[var(--color-text-muted)]">Removed from future runs</p>
                )}
                <time
                  dateTime={message.createdAt}
                  className="mt-1 block text-right text-xs text-[var(--color-text-muted)]"
                >
                  {new Date(message.createdAt).toLocaleString(undefined, {
                    month: 'short',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                    second: '2-digit',
                  })}
                </time>
                {message.id === latestMessage?.id && message.chooseTranscriptSource && !isRunning && (
                  <div className="mt-3 flex flex-col gap-2">
                    <button
                      type="button"
                      disabled={isBusy}
                      onClick={() => void sendMessage('Use original transcript')}
                      className="rounded-[10px] border border-border px-3 py-2 text-left disabled:opacity-50"
                    >
                      Original transcript
                    </button>
                    <button
                      type="button"
                      disabled={isBusy || !message.hasPreviousTranscript}
                      onClick={() => void sendMessage('Use previous enhancement')}
                      className="rounded-[10px] border border-border px-3 py-2 text-left disabled:opacity-50"
                    >
                      Previous enhancement
                    </button>
                  </div>
                )}
                {message.confirmationId === confirmationId && confirmationId && !isRunning && (
                  <button
                    type="button"
                    disabled={isBusy}
                    onClick={() => void sendMessage('Yes, run')}
                    className="mt-3 rounded-[10px] bg-[var(--color-success-text)] px-4 py-2 font-semibold text-[var(--color-surface)] disabled:opacity-50"
                  >
                    Yes, run
                  </button>
                )}
              </div>
            ))}
            {isRunning && (
              <div role="status" className="flex flex-col gap-2">
                <span>Super enhancement is running. You can send another message when it finishes.</span>
                <Typing className="bg-[var(--color-surface-muted)]" />
              </div>
            )}
          </div>
          <div className="flex shrink-0 flex-col gap-2 text-sm" aria-label="Stages for the next run">
            <p className="text-[var(--color-text-muted)]">Next run · review before starting</p>
            <button
              type="button"
              disabled={isBusy || !state || messagesQuery.isPending || messagesQuery.isError}
              className="rounded-[10px] border border-border bg-[var(--color-surface-muted)] px-3 py-2 text-left transition-opacity hover:opacity-80 disabled:cursor-not-allowed disabled:opacity-50"
              onClick={() =>
                void sendMessage(`${stageOptions.speakerCorrection ? 'Disable' : 'Enable'} speaker correction`)
              }
            >
              {stageOptions.speakerCorrection ? 'Disable' : 'Enable'} speaker correction
            </button>
            <button
              type="button"
              disabled={isBusy || !state || messagesQuery.isPending || messagesQuery.isError}
              className="rounded-[10px] border border-border bg-[var(--color-surface-muted)] px-3 py-2 text-left transition-opacity hover:opacity-80 disabled:cursor-not-allowed disabled:opacity-50"
              onClick={() =>
                void sendMessage(`${stageOptions.audioRelistening ? 'Disable' : 'Enable'} audio relistening`)
              }
            >
              {stageOptions.audioRelistening ? 'Disable' : 'Enable'} audio relistening
            </button>
            <button
              type="button"
              disabled={isBusy || !state || messagesQuery.isPending || messagesQuery.isError}
              className="rounded-[10px] border border-border bg-[var(--color-surface-muted)] px-3 py-2 text-left transition-opacity hover:opacity-80 disabled:cursor-not-allowed disabled:opacity-50"
              onClick={() => void sendMessage(`${stageOptions.verifyEdits ? 'Disable' : 'Enable'} edit verification`)}
            >
              {stageOptions.verifyEdits ? 'Disable' : 'Enable'} edit verification
            </button>
            <p className="rounded-[10px] border border-border bg-[var(--color-surface-muted)] px-3 py-2 text-[var(--color-text-muted)]">
              User instructions:{' '}
              {customInstructions.length ? `enabled (${customInstructions.length})` : 'disabled · add a message'}
            </p>
            {!stageOptions.verifyEdits && <p role="note">Edits will be applied without independent model review.</p>}
            <p className="text-xs text-[var(--color-text-muted)]">
              Error detection, editing, safety checks, and saving results always run. Click an instruction to configure
              the next run.
            </p>
          </div>
          <button
            type="button"
            disabled={isBusy || !state || stateQuery.isError || messagesQuery.isPending || messagesQuery.isError}
            onClick={() => void sendMessage('Run super enhancement')}
            className="shrink-0 rounded-[10px] bg-[var(--color-success-text)] px-4 py-3 text-sm font-semibold text-[var(--color-surface)] shadow-sm transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Run super enhancement
          </button>
          <Input
            label="Message"
            aria-invalid={!isDraftEnglish}
            aria-describedby={isDraftEnglish ? undefined : 'super-enhancement-message-language'}
            aria-label="Message"
            placeholder="Write an instruction in English"
            value={draft}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => setDraft(event.target.value)}
            onKeyUp={(event) => {
              if (event.key === 'Enter') {
                void sendMessage();
              }
            }}
            disabled={isBusy || !state || stateQuery.isError || messagesQuery.isPending || messagesQuery.isError}
            adornment={
              <IconButtonClean
                type="button"
                aria-label="Send"
                disabled={
                  isBusy ||
                  !draft.trim() ||
                  !isDraftEnglish ||
                  !state ||
                  stateQuery.isError ||
                  messagesQuery.isPending ||
                  messagesQuery.isError
                }
                onClick={() => void sendMessage()}
              >
                <SendIcon aria-hidden="true" />
              </IconButtonClean>
            }
          />
          {!isDraftEnglish && (
            <p id="super-enhancement-message-language" role="alert" className="text-xs text-[var(--color-text-muted)]">
              Please write your message in English only.
            </p>
          )}
        </aside>
      </div>
    </>
  );
};
